// src/v3/ui/scheduler/cron-form.ui.ts

import { CronExpressionParser } from "cron-parser";
import cronstrue from "cronstrue";

export const CRON_FREQUENCIES = ["daily", "weekly", "custom"] as const;
export type CronFrequency = (typeof CRON_FREQUENCIES)[number];
export const CRON_FREQUENCY_OBJ = {
  DAILY: CRON_FREQUENCIES[0],
  WEEKLY: CRON_FREQUENCIES[1],
  CUSTOM: CRON_FREQUENCIES[2],
} as const;

export interface CronFormState {
  frequency: CronFrequency;
  minute: string;
  hour: string;
  dayOfWeek: string;
  raw: string;
}

const isSingleValue = (v: string): boolean => /^\d+$/.test(v);

export function parseCronToForm(expr: string): CronFormState {
  const parts = expr.trim().split(/\s+/);
  if (parts.length === 5) {
    const min = parts[0] ?? "0";
    const hour = parts[1] ?? "5";
    const dom = parts[2] ?? "*";
    const month = parts[3] ?? "*";
    const dow = parts[4] ?? "*";
    // Only simple single-value fields map onto the daily/weekly pickers.
    // Compound values (6,23 / */30 / ranges) and non-wildcard dom/month
    // go to "custom" so the raw expression is preserved untouched.
    if (
      isSingleValue(min) &&
      isSingleValue(hour) &&
      dom === "*" &&
      month === "*"
    ) {
      if (dow === "*") {
        return {
          frequency: CRON_FREQUENCY_OBJ.DAILY,
          minute: min,
          hour,
          dayOfWeek: "0",
          raw: expr,
        };
      }
      if (isSingleValue(dow)) {
        return {
          frequency: CRON_FREQUENCY_OBJ.WEEKLY,
          minute: min,
          hour,
          dayOfWeek: dow,
          raw: expr,
        };
      }
    }
  }
  return {
    frequency: CRON_FREQUENCY_OBJ.CUSTOM,
    minute: "0",
    hour: "5",
    dayOfWeek: "0",
    raw: expr,
  };
}

export function formToCron(
  frequency: CronFrequency,
  minute: string,
  hour: string,
  dayOfWeek: string,
): string {
  if (frequency === CRON_FREQUENCY_OBJ.DAILY) {
    return `${minute} ${hour} * * *`;
  }
  if (frequency === CRON_FREQUENCY_OBJ.WEEKLY) {
    return `${minute} ${hour} * * ${dayOfWeek}`;
  }
  return `${minute} ${hour} * * ${dayOfWeek}`;
}

export function describeCron(expr: string): string {
  try {
    return cronstrue.toString(expr, { verbose: false });
  } catch {
    return expr;
  }
}

export function getNextRun(expr: string): string | null {
  try {
    const interval = CronExpressionParser.parse(expr);
    const next = interval.next().toISOString();
    return next;
  } catch {
    return null;
  }
}

export function validateCron(expr: string): boolean {
  try {
    CronExpressionParser.parse(expr);
    return true;
  } catch {
    return false;
  }
}
